import React from 'react';
import { Pressable } from 'react-native';

import { Stack } from '@/components/layout';
import { Text } from '@/components/ui';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors, withOpacity } from '@/theme/colors';

export type RelationshipOption = {
  value: string;
  label: string;
};

type Props = {
  options: RelationshipOption[];
  value: string | null;
  onChange: (value: string) => void;
};

/**
 * Single-select, wrapping pill group for the invitee's relationship
 * (Parent / Grandparent / Coach / Other). Flat pills per the invite design.
 */
export default function RelationshipSelector({ options, value, onChange }: Props) {
  const styles = useStyles();
  const { scale } = useScaling();

  return (
    <Stack direction="row" wrap="wrap" gap={scale(4)} width="100%">
      {options.map(option => {
        const selected = option.value === value;
        return (
          <Pressable
            key={option.value}
            accessibilityRole="button"
            accessibilityState={{ selected }}
            onPress={() => onChange(option.value)}
            style={[styles.pill, selected ? styles.pillSelected : styles.pillUnselected]}>
            <Text
              variant="Font Guardian App/Subtitle/Subtitle 12 Semibold"
              style={selected ? styles.labelSelected : styles.label}>
              {option.label}
            </Text>
          </Pressable>
        );
      })}
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  pill: {
    height: scale(40),
    paddingHorizontal: scale(16),
    paddingVertical: scale(10),
    borderRadius: scale(40),
    borderCurve: 'continuous',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
  },
  pillSelected: {
    backgroundColor: colors['0'],
    borderColor: colors['Level 1'],
  },
  pillUnselected: {
    backgroundColor: withOpacity(colors['0'], 0.6),
    borderColor: colors['0'],
  },
  label: {
    color: colors['10'],
  },
  labelSelected: {
    color: guardianColors['Main/Main'],
  },
}));
